/**
 * useParentThemeScope — bascule le design system sur le thème parent le temps de l'espace parent.
 *
 * Les thèmes partagent le même code de composant et ne diffèrent que par leurs jetons
 * (`design-system/theme/*`). Entrer dans l'espace parent revient donc à changer de thème, pas
 * d'écran : le fond de page, la lavande des encarts et la couleur d'univers suivent d'eux-mêmes.
 * À la sortie on rend la main au thème d'avant — le parent revient souvent sur un compte élève.
 *
 * **Clair ou sombre : c'est le parent qui tranche**, dans la feuille « Changer de profil »
 * (Automatique, Clair, Sombre — `parentAppearancePreference`) ; Automatique, le défaut, suit
 * l'apparence du système — voir `parentThemeFor`. Le choix se relit à chaque changement de
 * préférence ou d'apparence, sans quitter l'écran.
 *
 * Le crochet porte donc DEUX portées, et pas une : le thème du design system, et — sur iOS —
 * l'apparence native, celle que le clavier et les alertes du système suivent (`parentAppearance`).
 * La seconde ne peut pas vivre dans un thème : elle déborde de React.
 */
import { useEffect, useRef } from "react"
import { Platform, useColorScheme } from "react-native"
import { UnistylesRuntime } from "react-native-unistyles"

import { applyAppearanceMove, parentAppearanceAt } from "./parentAppearance"
import { useParentAppearancePreference } from "./parentAppearancePreference"
import { parentThemeFor } from "./parentThemeFor"

export function useParentThemeScope(): void {
    const preference = useParentAppearancePreference()
    // Tant que Clair ou Sombre est forcé sur iOS, `useColorScheme()` rend la valeur forcée, pas le
    // système : la règle ne s'en sert donc qu'en Automatique, où l'apparence vient d'être rendue au
    // système et où la lecture redevient la sienne dès que celui-ci l'a signalé.
    const parentTheme = parentThemeFor(useColorScheme(), preference)
    const previousRef = useRef<typeof UnistylesRuntime.themeName>(undefined)

    // Le thème d'avant se retient UNE fois, à l'entrée. Le relire à chaque bascule d'apparence le
    // remplacerait par le thème parent lui-même, et la sortie rendrait la main au mauvais univers.
    useEffect(() => {
        previousRef.current = UnistylesRuntime.themeName

        return () => {
            const previous = previousRef.current
            if (previous) UnistylesRuntime.setTheme(previous)
            applyAppearanceMove(parentAppearanceAt("exit", Platform.OS))
        }
    }, [])

    // L'app démarre épinglée au clair (`index.js`), sinon `useColorScheme()` dirait toujours
    // « clair » sur iOS. L'espace parent SEUL pose sa propre apparence — à l'entrée, puis à chaque
    // choix fait dans la feuille : elle emporte avec elle le clavier et les alertes du système, que
    // le reste de l'app veut clairs.
    useEffect(() => {
        applyAppearanceMove(parentAppearanceAt("enter", Platform.OS, preference))
    }, [preference])

    useEffect(() => {
        if (UnistylesRuntime.themeName !== parentTheme) UnistylesRuntime.setTheme(parentTheme)
    }, [parentTheme])
}
